import { useQuery } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SchedulingAsync as Async,
  clockTime,
  isCutoffPassed,
  problemMessage,
  shiftDate,
} from '@/features/scheduling';
import type { Booking } from '@/features/scheduling';

import { SlotChips, dateStrip, dayLabel } from './components';
import { useBookingDeps } from './context';
import { BOOKING_KEYS, STRIP_DAYS, bookingItems } from './steps';

/**
 * §6's client screens either side of the journey: the landing page, `/booking/:ref` with
 * R6's reschedule and cancel, and `/me/bookings`.
 *
 * `/booking/:ref` has two ways in. A signed link (`?token=`) reads
 * `GET /public/bookings/{ref}` — deliberately less than a `BookingOut`, so a forwarded
 * link shows an appointment and not a client record. A client with an OTP session reads
 * their own `GET /bookings`, which is the only view that can offer a change, because only
 * that call proves who is asking.
 */

/** §6 client `/`. */
export function LandingPage({
  onStart,
  onMine,
  signIn = null,
}: {
  onStart: () => void;
  onMine: () => void;
  /**
   * The customer sign-in, composed by the app (a feature may not import an app). Rendered
   * beside the landing section, not inside it: the section is the page's one heading and its
   * two buttons.
   */
  signIn?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation('booking');
  return (
    <div className="flex flex-col gap-4">
      <section data-testid="booking-landing" className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold">{t('landing.title')}</h1>
        <p>{t('landing.lead')}</p>
        <button
          type="button"
          data-testid="landing-start"
          onClick={onStart}
          className="rounded bg-primary px-4 py-3 text-primary-foreground"
        >
          {t('landing.start')}
        </button>
        <button
          type="button"
          data-testid="landing-mine"
          onClick={onMine}
          className="rounded border border-border px-4 py-3"
        >
          {t('landing.mine')}
        </button>
      </section>
      {signIn}
    </div>
  );
}

/** §6's `CancelDialog`. */
export function CancelDialog({
  reference,
  busy,
  onCancel,
  onKeep,
}: {
  reference: string;
  busy: boolean;
  onCancel: (reason: string) => void;
  onKeep: () => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const [reason, setReason] = useState('');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('detail.cancelTitle', { ref: reference })}
      data-testid="cancel-dialog"
      className="rounded border border-border p-3"
    >
      <h3 className="font-medium">{t('detail.cancelTitle', { ref: reference })}</h3>
      <label className="flex flex-col text-sm">
        {t('detail.cancelReason')}
        <input
          type="text"
          data-testid="cancel-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          className="rounded border border-border px-2 py-1"
        />
      </label>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="cancel-confirm"
          disabled={busy}
          onClick={() => {
            onCancel(reason.trim());
          }}
          className="rounded bg-destructive px-3 py-2 text-primary-foreground disabled:opacity-40"
        >
          {t('detail.cancelConfirm')}
        </button>
        <button
          type="button"
          data-testid="cancel-keep"
          onClick={onKeep}
          className="rounded border border-border px-3 py-2"
        >
          {t('detail.cancelKeep')}
        </button>
      </div>
    </div>
  );
}

/** §6's `RescheduleFlow`: the date strip and slot chips again, then one POST. */
export function RescheduleFlow({
  booking,
  busy,
  onMove,
  onKeep,
}: {
  booking: Booking;
  busy: boolean;
  onMove: (items: ReturnType<typeof bookingItems>) => void;
  onKeep: () => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone } = useBookingDeps();
  const active = booking.items.filter((item) => item.is_active);
  const first = active[0];
  const serviceIds = active.map((item) => item.service_id);
  const addonIds = first?.addon_ids ?? [];
  const [date, setDate] = useState(() => (first?.start_at ?? '').slice(0, 10));
  const [picked, setPicked] = useState<{ start_at: string; staff_id: string } | null>(null);
  const days = date === '' ? [] : dateStrip(date, STRIP_DAYS);

  const slots = useQuery({
    queryKey: BOOKING_KEYS.slots(date, serviceIds, null, addonIds),
    queryFn: () => api.availability({ date, service_ids: serviceIds }),
    enabled: date !== '',
  });

  if (first === undefined) {
    // Every item has been dropped by an earlier reschedule: there is nothing left to move,
    // and the api would refuse a `RescheduleIn` with no items anyway.
    return (
      <p role="status" data-testid="reschedule-locked">
        {t('detail.locked')}
      </p>
    );
  }

  return (
    <div data-testid="reschedule-flow" className="flex flex-col gap-2">
      <ul data-testid="reschedule-dates" className="flex gap-2 overflow-x-auto">
        {days.map((day) => (
          <li key={day}>
            <button
              type="button"
              aria-pressed={day === date}
              data-testid={`reschedule-date-${day}`}
              onClick={() => {
                setDate(day);
                setPicked(null);
              }}
              className={`whitespace-nowrap rounded border px-3 py-2 ${
                day === date ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
              }`}
            >
              {dayLabel(day, timezone)}
            </button>
          </li>
        ))}
      </ul>
      <Async pending={slots.isPending} error={slots.error} data={slots.data}>
        {(value) => (
          <SlotChips
            slots={value.slots}
            timezone={timezone}
            chosen={
              picked === null
                ? null
                : { start_at: picked.start_at, staff_id: picked.staff_id, end_at: '', station_id: null }
            }
            nextFree={shiftDate(date, 1)}
            onChoose={(slot) => {
              setPicked({ start_at: slot.start_at, staff_id: slot.staff_id });
            }}
          />
        )}
      </Async>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="reschedule-move"
          disabled={picked === null || busy}
          onClick={() => {
            onMove(bookingItems(serviceIds, addonIds, picked));
          }}
          className="rounded bg-primary px-3 py-2 text-primary-foreground disabled:opacity-40"
        >
          {t('detail.reschedule')}
        </button>
        <button
          type="button"
          data-testid="reschedule-keep"
          onClick={onKeep}
          className="rounded border border-border px-3 py-2"
        >
          {t('detail.keep')}
        </button>
      </div>
    </div>
  );
}

/** The booking a signed link shows — no client, no notes, no policy internals. */
export function PublicBookingView({
  reference,
  token,
}: {
  reference: string;
  token: string;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone } = useBookingDeps();
  const query = useQuery({
    queryKey: BOOKING_KEYS.publicBooking(reference, token),
    queryFn: () => api.publicBooking(reference, token),
  });

  return (
    <section data-testid="public-booking" className="flex flex-col gap-2">
      <Async pending={query.isPending} error={query.error} data={query.data}>
        {(booking) => (
          <>
            <h2 className="text-lg font-semibold">
              {t('detail.title', { ref: booking.ref })}
            </h2>
            <p data-testid="public-when" className="tabular-nums">
              {dayLabel(booking.start_at.slice(0, 10), timezone)} ·{' '}
              {clockTime(booking.start_at, timezone)}
            </p>
            <p data-testid="public-status">{booking.status}</p>
            {booking.may_change ? null : (
              <p role="status" data-testid="public-locked">
                {t('detail.locked')}
              </p>
            )}
          </>
        )}
      </Async>
    </section>
  );
}

/** §6 `/booking/:ref` for a signed-in client: reschedule and cancel (R6). */
export function MyBookingView({ bookingId }: { bookingId: string }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone, newIdempotencyKey } = useBookingDeps();
  const [mode, setMode] = useState<'view' | 'move' | 'cancel'>('view');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'moved' | 'cancelled' | null>(null);

  const query = useQuery({
    queryKey: BOOKING_KEYS.one(bookingId),
    queryFn: () => api.readBooking(bookingId),
  });

  const run = (work: Promise<Booking>, outcome: 'moved' | 'cancelled'): void => {
    setBusy(true);
    setError(null);
    work
      .then(() => {
        setDone(outcome);
        setMode('view');
        return query.refetch();
      })
      .catch((reason: unknown) => {
        setError(reason);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section data-testid="my-booking" className="flex flex-col gap-3">
      <Async pending={query.isPending} error={query.error} data={query.data}>
        {(booking) => (
          // BP-36 rule 5: from 768 px of page the booking and what is being done to it (the
          // new dates and slots, or the cancel form) sit side by side; below, one column.
          <div
            data-testid="my-booking-columns"
            className="flex flex-col gap-3 @3xl:grid @3xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] @3xl:items-start @3xl:gap-6"
          >
            <div className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold">{t('detail.title', { ref: booking.ref })}</h2>
              <p data-testid="booking-status">{booking.status}</p>
              {booking.items
                .filter((item) => item.is_active)
                .map((item) => (
                  <p key={item.id} data-testid={`booking-when-${item.id}`} className="tabular-nums">
                    {dayLabel(item.start_at.slice(0, 10), timezone)} ·{' '}
                    {clockTime(item.start_at, timezone)}
                  </p>
                ))}

              {done === null ? null : (
                <p role="status" data-testid="booking-done">
                  {t(done === 'moved' ? 'detail.moved' : 'detail.cancelled')}
                </p>
              )}
              {error === null ? null : (
                <p role="alert" data-testid="booking-error">
                  {isCutoffPassed(error)
                    ? t('detail.locked')
                    : problemMessage(error, t('detail.locked'))}
                </p>
              )}

              {mode === 'view' ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="open-reschedule"
                    onClick={() => {
                      setMode('move');
                    }}
                    className="rounded border border-border px-3 py-2"
                  >
                    {t('detail.reschedule')}
                  </button>
                  <button
                    type="button"
                    data-testid="open-cancel"
                    onClick={() => {
                      setMode('cancel');
                    }}
                    className="rounded border border-border px-3 py-2"
                  >
                    {t('detail.cancel')}
                  </button>
                </div>
              ) : null}
            </div>
            {mode === 'move' ? (
              <RescheduleFlow
                booking={booking}
                busy={busy}
                onMove={(items) => {
                  run(api.reschedule(booking.id, items, newIdempotencyKey()), 'moved');
                }}
                onKeep={() => {
                  setMode('view');
                }}
              />
            ) : null}
            {mode === 'cancel' ? (
              <CancelDialog
                reference={booking.ref}
                busy={busy}
                onCancel={(reason) => {
                  run(api.cancel(booking.id, reason, newIdempotencyKey()), 'cancelled');
                }}
                onKeep={() => {
                  setMode('view');
                }}
              />
            ) : null}
          </div>
        )}
      </Async>
    </section>
  );
}

/** §6 `/me/bookings`. */
export function MyBookingsPage({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone } = useBookingDeps();
  const query = useQuery({ queryKey: BOOKING_KEYS.mine, queryFn: () => api.myBookings() });

  return (
    <section data-testid="my-bookings" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('mine.title')}</h2>
      <Async pending={query.isPending} error={query.error} data={query.data}>
        {(page) =>
          page.items.length === 0 ? (
            <p role="status" data-testid="no-bookings">
              {t('mine.none')}
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 @3xl:grid-cols-2 @6xl:grid-cols-3">
              {page.items.map((booking) => (
                <li key={booking.id}>
                  <button
                    type="button"
                    data-testid={`my-booking-${booking.id}`}
                    onClick={() => {
                      onOpen(booking.id);
                    }}
                    className="w-full rounded border border-border p-3 text-left"
                  >
                    <span className="block font-medium">{booking.ref}</span>
                    <span className="block tabular-nums">
                      {booking.items[0] === undefined
                        ? ''
                        : `${dayLabel(booking.items[0].start_at.slice(0, 10), timezone)} · ${clockTime(
                            booking.items[0].start_at,
                            timezone,
                          )}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        }
      </Async>
    </section>
  );
}
